/**
 * Import Engine
 * Takes validated import data (zero errors) and executes INSERT/UPDATE operations
 * against the database via the Dynamic API.
 * Processes tabs in dependency order, resolves FK display values to IDs,
 * tracks newly created records for cross-tab references.
 */
import { dynamicApi } from '../services/api';
import { TABLE_CONFIG } from './importTemplateBuilder';

// Fields to exclude from insert/update payloads (system-managed or calculated)
const EXCLUDE_FIELDS = ['ID', 'Register'];

// Fields to auto-stamp on every insert/update
const STAMP_FIELDS = ['Rev_Date', 'Rev_User'];

/**
 * Execute the bulk import using validated data from the validator.
 *
 * @param {Object} params
 * @param {Object} params.valResult - The full validation result object
 * @param {Function} params.onProgress - Called with { tabIndex, tabCount, tabName, phase, inserted, updated, failed }
 * @param {string} params.user - Logged-in username for Rev_User stamping
 * @returns {Object} Import results
 */
export async function executeImport({ valResult, onProgress, user }) {
  const { recordsByTab, fkIdToDisplayMap, fieldFkMap, metadataMap } = valResult;

  // Build reverse FK map: displayValue -> ID (for converting import display values to FK IDs)
  const displayToIdMap = {}; // fkTable -> Map<displayValueLower, id>
  for (const [fkTable, idMap] of Object.entries(fkIdToDisplayMap)) {
    const reverseMap = new Map();
    for (const [id, displayValue] of idMap.entries()) {
      reverseMap.set(String(displayValue).toLowerCase(), parseInt(id, 10));
    }
    displayToIdMap[fkTable] = reverseMap;
  }

  // Track newly created records for cross-tab FK resolution
  // Structure: newlyCreatedMap[dbTable] = Map<displayValueLower, newId>
  const newlyCreatedMap = {};

  const resultsByTab = {};
  let totalInserted = 0;
  let totalUpdated = 0;
  let totalSkipped = 0;
  let totalFailed = 0;

  const tabCount = TABLE_CONFIG.length;

  for (let tabIndex = 0; tabIndex < TABLE_CONFIG.length; tabIndex++) {
    const { tabName, dbTable } = TABLE_CONFIG[tabIndex];
    const records = recordsByTab[tabName];

    if (!records || records.length === 0) {
      resultsByTab[tabName] = { inserted: 0, updated: 0, skipped: 0, failed: 0, errors: [] };
      onProgress?.({ tabIndex, tabCount, tabName, phase: 'skipped', inserted: 0, updated: 0, failed: 0 });
      continue;
    }

    const tabResult = { inserted: 0, updated: 0, skipped: 0, failed: 0, errors: [] };
    const metadata = metadataMap[dbTable] || [];

    // Find the match field(s) display field for newlyCreatedMap keying
    // Use the first matchField as the key for tracking new records
    const tableConfig = TABLE_CONFIG.find(t => t.dbTable === dbTable);

    onProgress?.({ tabIndex, tabCount, tabName, phase: 'processing', inserted: 0, updated: 0, failed: 0 });

    // Process inserts first, then updates
    const inserts = records.filter(r => r.status === 'insert');
    const updates = records.filter(r => r.status === 'update');
    const skips = records.filter(r => r.status === 'skip');
    tabResult.skipped = skips.length;

    // --- INSERTS ---
    for (const record of inserts) {
      try {
        const payload = buildPayload(record.rowValues, metadata, fieldFkMap, displayToIdMap, newlyCreatedMap, user);
        const response = await dynamicApi.create(dbTable, payload);
        const newId = response.data?.id;

        // Track the new record for downstream FK resolution
        if (newId) {
          trackNewRecord(dbTable, record, newId, metadata, newlyCreatedMap);
        }

        tabResult.inserted++;
      } catch (err) {
        tabResult.failed++;
        tabResult.errors.push({
          row: record.row,
          matchKey: record.matchKey,
          operation: 'insert',
          error: err.response?.data?.message || err.response?.data?.error || err.message
        });
      }
    }

    // --- UPDATES ---
    for (const record of updates) {
      try {
        // Only send changed fields + stamp fields
        const changedFieldNames = record.changedFields.map(c => c.field);
        const updateValues = {};
        for (const fieldName of changedFieldNames) {
          updateValues[fieldName] = record.rowValues[fieldName];
        }
        // Always stamp revision fields
        updateValues['Rev_Date'] = new Date().toISOString();
        updateValues['Rev_User'] = user;

        const payload = buildPayload(updateValues, metadata, fieldFkMap, displayToIdMap, newlyCreatedMap, user, true);
        await dynamicApi.update(dbTable, record.existingId, payload);
        tabResult.updated++;
      } catch (err) {
        tabResult.failed++;
        tabResult.errors.push({
          row: record.row,
          matchKey: record.matchKey,
          operation: 'update',
          error: err.response?.data?.message || err.response?.data?.error || err.message
        });
      }
    }

    resultsByTab[tabName] = tabResult;
    totalInserted += tabResult.inserted;
    totalUpdated += tabResult.updated;
    totalSkipped += tabResult.skipped;
    totalFailed += tabResult.failed;

    onProgress?.({
      tabIndex, tabCount, tabName,
      phase: 'complete',
      inserted: tabResult.inserted,
      updated: tabResult.updated,
      failed: tabResult.failed
    });
  }

  return {
    success: totalFailed === 0,
    totalInserted,
    totalUpdated,
    totalSkipped,
    totalFailed,
    resultsByTab
  };
}

/**
 * Build the API payload from row values.
 * Resolves FK display values to integer IDs and handles type conversions.
 */
function buildPayload(rowValues, metadata, fieldFkMap, displayToIdMap, newlyCreatedMap, user, isUpdate = false) {
  const payload = {};

  for (const [fieldName, value] of Object.entries(rowValues)) {
    // Skip excluded fields
    if (EXCLUDE_FIELDS.includes(fieldName)) continue;

    // Handle stamp fields
    if (fieldName === 'Rev_Date') {
      payload[fieldName] = new Date().toISOString();
      continue;
    }
    if (fieldName === 'Rev_User') {
      payload[fieldName] = user;
      continue;
    }

    let resolvedValue = value;

    // Resolve FK display values to IDs
    const fkTable = fieldFkMap[fieldName];
    if (fkTable && resolvedValue && String(resolvedValue).trim() !== '') {
      const displayLower = String(resolvedValue).trim().toLowerCase();

      // Check newlyCreatedMap first (records created during this import session)
      if (newlyCreatedMap[fkTable]?.has(displayLower)) {
        resolvedValue = newlyCreatedMap[fkTable].get(displayLower);
      }
      // Then check the pre-existing display-to-ID map
      else if (displayToIdMap[fkTable]?.has(displayLower)) {
        resolvedValue = displayToIdMap[fkTable].get(displayLower);
      } else {
        console.warn(`[Import] FK resolution failed: ${fieldName}="${resolvedValue}" not found in ${fkTable}`);
      }
    }

    // Convert empty strings to null
    if (resolvedValue === '' || resolvedValue === undefined) {
      resolvedValue = null;
    }

    // Convert time strings to proper format for the backend
    if (resolvedValue && typeof resolvedValue === 'string') {
      const fieldMeta = metadata.find(f => f.field_Name === fieldName);
      if (fieldName.toLowerCase().includes('time') && fieldMeta?.uI_Type === 'text') {
        // Keep time as string — backend ConvertJsonValue will handle it
      }
    }

    payload[fieldName] = resolvedValue;
  }

  // Auto-stamp if not already set
  if (!isUpdate) {
    if (!payload['Rev_Date']) payload['Rev_Date'] = new Date().toISOString();
    if (!payload['Rev_User']) payload['Rev_User'] = user;
  }

  return payload;
}

/**
 * Track a newly created record so downstream tabs can resolve FK references to it.
 * Keys the record by its "display value" — the value that downstream FK fields reference.
 */
function trackNewRecord(dbTable, record, newId, metadata, newlyCreatedMap) {
  if (!newlyCreatedMap[dbTable]) {
    newlyCreatedMap[dbTable] = new Map();
  }

  // For each field in this table's metadata that serves as a FK display field for another table,
  // store the mapping. But the simplest approach: store by each field that could be a display value.
  // The common pattern is: the table's "natural key" field serves as the FK display field.
  // We can determine this by looking at which fields in OTHER tables' metadata reference this table.

  // For now, store every non-empty field value as a potential key (lowercase)
  // This is broad but safe — downstream lookups will only match on the right field
  for (const [fieldName, value] of Object.entries(record.rowValues)) {
    if (value && String(value).trim() !== '' && !EXCLUDE_FIELDS.includes(fieldName)) {
      newlyCreatedMap[dbTable].set(String(value).trim().toLowerCase(), newId);
    }
  }
}
